import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { OrganisationService } from '@principle-theorem/ng-principle-shared';
import { DialogPresets, TrackByFunctions } from '@principle-theorem/ng-shared';
import {
  type IClinicalNote,
  type IPatient,
} from '@principle-theorem/principle-core/interfaces';
import {
  ArchivedDocument,
  filterUndefined,
  Firestore,
  HISTORY_DATE_FORMAT,
  isSameRef,
  multiSwitchMap,
  snapshot,
  snapshotDefined,
  type WithRef,
} from '@principle-theorem/shared';
import { type Observable, ReplaySubject, combineLatest } from 'rxjs';
import { map, startWith, switchMap } from 'rxjs/operators';
import { ClinicalNoteEditDialogComponent } from '../clinical-note-edit-dialog/clinical-note-edit-dialog.component';
import { ClinicalNote, ScribeSession } from '@principle-theorem/principle-core';
import { ClinicalNotesArchiveDialogComponent } from '../clinical-notes-archive-dialog/clinical-notes-archive-dialog.component';
import { getSchemaText } from '@principle-theorem/editor';
import {
  type IScribeReviewDialogData,
  type IScribeReviewDialogResult,
  ScribeReviewDialogComponent,
} from '../scribe-review-dialog/scribe-review-dialog.component';

interface IClinicalNoteWithHistory {
  note: WithRef<IClinicalNote>;
  history: ArchivedDocument<IClinicalNote>[];
  scribeNoteCount: number;
}

@Component({
  selector: 'pr-clinical-notes-list',
  templateUrl: './clinical-notes-list.component.html',
  styleUrls: ['./clinical-notes-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ClinicalNotesListComponent {
  patient$ = new ReplaySubject<WithRef<IPatient>>(1);
  clinicalNotes$ = new ReplaySubject<WithRef<IClinicalNote>[]>(1);
  notesWithHistory$: Observable<IClinicalNoteWithHistory[]>;
  trackByNote = TrackByFunctions.ref<WithRef<IClinicalNote>>();
  readonly dateFormat = HISTORY_DATE_FORMAT;

  @Input()
  set clinicalNotes(clinicalNotes: WithRef<IClinicalNote>[]) {
    if (clinicalNotes) {
      this.clinicalNotes$.next(clinicalNotes);
    }
  }

  @Input()
  set patient(patient: WithRef<IPatient>) {
    if (patient) {
      this.patient$.next(patient);
    }
  }

  constructor(
    private _dialog: MatDialog,
    private _organisation: OrganisationService
  ) {
    this.notesWithHistory$ = combineLatest([
      this.clinicalNotes$.pipe(
        multiSwitchMap((note) =>
          ClinicalNote.history$(note).pipe(
            map((history) => ({ note, history }))
          )
        )
      ),
      this._scribeNoteCounts$(),
    ]).pipe(
      map(([notes, scribeNoteCounts]) =>
        notes.map(({ note, history }) => ({
          note,
          history,
          scribeNoteCount:
            scribeNoteCounts.get(
              ScribeSession.contextKey(note.owner.ref, note.recordDate)
            ) ?? 0,
        }))
      )
    );
  }

  isNoteOwner$(note: WithRef<IClinicalNote>): Observable<boolean> {
    return this._organisation.staffer$.pipe(
      filterUndefined(),
      map((staffer) => isSameRef(staffer, note.owner))
    );
  }

  noteLockedMessage(note: WithRef<IClinicalNote>): string {
    return note.immutable ? `${note.owner.name} has locked this note` : '';
  }

  async toggleLock({ note, history }: IClinicalNoteWithHistory): Promise<void> {
    if (ClinicalNote.canArchive(note, history)) {
      const staffer = await snapshotDefined(this._organisation.staffer$);
      await ClinicalNote.archive(note, staffer.ref);
    }

    await Firestore.patchDoc(
      note.ref,
      { immutable: !note.immutable },
      undefined,
      {
        omitUpdateTimestamp: true,
      }
    );
  }

  async editClinicalNote(note: WithRef<IClinicalNote>): Promise<void> {
    const patient = await snapshot(this.patient$);
    const staffer = await Firestore.getDoc(note.owner.ref);
    const config = DialogPresets.large({
      height: '80%',
      data: {
        date: note.recordDate,
        patient,
        staffer,
      },
    });
    this._dialog.open(ClinicalNoteEditDialogComponent, config);
  }

  openHistoryDialog(history: ArchivedDocument<IClinicalNote>[]): void {
    const config = DialogPresets.large({
      height: '80%',
      data: { history },
    });
    this._dialog.open(ClinicalNotesArchiveDialogComponent, config);
  }

  isEmptyNote(note: WithRef<IClinicalNote>): boolean {
    return !getSchemaText(note.content).trim().length;
  }

  /**
   * Open the scribe review dialog on this row's exact key. The dialog offers
   * Insert only when the focused clinical note carries the same record date, so
   * a row for an earlier day is view-only by construction — no read-only flag
   * is passed, and none exists.
   */
  async openScribeNotes(note: WithRef<IClinicalNote>): Promise<void> {
    const patient = await snapshot(this.patient$);
    this._dialog.open<
      ScribeReviewDialogComponent,
      IScribeReviewDialogData,
      IScribeReviewDialogResult
    >(
      ScribeReviewDialogComponent,
      DialogPresets.extraLarge<IScribeReviewDialogData>({
        data: {
          patient,
          staffer: note.owner,
          recordDate: note.recordDate,
        },
      })
    );
  }

  /**
   * ONE live ready-session query for the whole list, bucketed by clinician +
   * charted day, because `@for` here is not virtualised: every note in the
   * patient's history is rendered at once, so a per-row query would open a live
   * Firestore listener per historical note. Counted from the session documents'
   * counters alone — the notes subcollection is queried only in the review
   * dialog.
   *
   * Seeded empty so the list still renders before the counts (or a patient)
   * arrive; a row simply shows no scribe button until its bucket lands.
   */
  private _scribeNoteCounts$(): Observable<Map<string, number>> {
    return this.patient$.pipe(
      switchMap((patient) => ScribeSession.readyForPatient$(patient)),
      map((sessions) => ScribeSession.readyNoteCountsByContext(sessions)),
      startWith(new Map<string, number>())
    );
  }
}
